/**
 * Centralised user-session helpers.
 *
 * Both AuthContext and the Axios interceptor need to build a `User` object
 * from a backend `LoginResponse` and persist it to localStorage.  Keeping
 * this logic in one place prevents the two from drifting apart.
 */
import type { LoginResponse } from '@api/types/auth';
import type { User, UserProfileData } from '@contexts/AuthContextStore';
import { STORAGE_KEYS } from '@constants/index';

const STORAGE_KEY = STORAGE_KEYS.USER;

// ── Build ────────────────────────────────────────────────────────────────────

/** Create a fresh `User` from a backend login / SSO response. */
export function buildUserFromResponse(
  data: LoginResponse,
  extras?: Partial<User>,
): User {
  return {
    id: data.userId!,
    displayName: data.displayName ?? null,
    email: data.email!,
    token: data.accessToken!,
    refreshToken: data.refreshToken,
    orgRole: (data.orgRole as User['orgRole']) || undefined,
    billingStatus: (data.billingStatus as User['billingStatus']) || 'PENDING',
    eulaAccepted: data.eulaAccepted,
    eulaUrl: data.eulaUrl,
    mfaEnabled: data.mfaEnabled ?? false,
    mfaEnforced: data.mfaEnforced ?? false,
    isPersonalOrg: data.isPersonalOrg ?? false,
    freeTierCredits: data.freeTierCredits ?? 0,
    orgId: data.orgId ?? null,
    orgName: data.orgName ?? null,
    planType: data.planType ?? null,
    orgCreatedAt: data.orgCreatedAt ?? null,
    userCreatedAt: data.userCreatedAt ?? null,
    lastLoginAt: data.lastLoginAt ?? null,
    hasCamAccess: data.hasCamAccess ?? false,
    userActivatedAt: data.userActivatedAt ?? null,
    orgUserCount: data.orgUserCount ?? 0,
    orgCreditsPerMonth: data.orgCreditsPerMonth ?? null,
    orgNextBillingDate: data.orgNextBillingDate ?? null,
    profileData: (data.profileData as UserProfileData | null) ?? null,
    hasCompletedOnboarding: data.hasCompletedOnboarding ?? false,
    onboardingSkipped: data.onboardingSkipped ?? false,
    groups: data.groups ?? [],
    ssoProvider: data.ssoProvider ?? null,
    ...extras,
  };
}

// ── Persist / read / clear ───────────────────────────────────────────────────

export function persistUserToStorage(user: User): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
}

export function getStoredUser(): User | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as User;
    // Backfill fields added after initial release so legacy sessions get safe defaults.
    data.isPersonalOrg = data.isPersonalOrg ?? false;
    data.freeTierCredits = data.freeTierCredits ?? 0;
    data.hasCamAccess = data.hasCamAccess ?? false;
    data.hasCompletedOnboarding = data.hasCompletedOnboarding ?? false;
    data.onboardingSkipped = data.onboardingSkipped ?? false;
    data.profileData = data.profileData ?? null;
    return data;
  } catch {
    return null;
  }
}

export function clearStoredUser(): void {
  localStorage.removeItem(STORAGE_KEY);
}
